/**
 * @file BoardroomRecorderPage.tsx
 * @brief Live durable boardroom capture — closer to mobile MeetingRecorder UX.
 *
 * @details
 * Phase B: Stripe preauth at start (skip on Continue when draft already has a
 * hold), hold / goggles / End & Process with paymentIntentId threaded into
 * create. Live cost/rate is intentionally NOT shown during capture — ticking
 * money mid-meeting scares users; billing stays hold → settle after upload.
 *
 * UI preview: set BOARDROOM_RECORDER_UI_PREVIEW or open `?uiPreview=1` to style
 * the page without mic/billing (mirrors mobile MeetingRecorder preview).
 * Card chrome: BOARDROOM_RECORDER_SHOW_CARD (off in live — content on page canvas).
 *
 * Meeting Notes (ADR-030, DES 07-10-2026, the phone's b1714c0, af707a1,
 * 01952e5, cabea16, deed9d4, a6d85c3, 625c775): the title is the meeting's or
 * "Meeting notes"; no avatar row, no agenda, no Action Goggles or marks at
 * all; the notepad is "Take notes" with a "Meeting title (optional)" field
 * (prefilled from Setup) whose text names the meeting when Setup gave none,
 * and no "Attach an existing note"; the slide reads "Slide to end". The
 * tier is the draft's (useDurableRecorder `state.tier`), so a recovered
 * Notes draft keeps the Notes chrome. Full mode: the to-do mark reads
 * "To-do" (the phone's c22f7e3); mark times were already m:ss from
 * timeInSecs here (2c64b14).
 *
 * Calm recorder (DES 19-10-2026), Meeting Notes only: a small "Listening"
 * label (no pulse), the mic with more room around it, the plain notepad, and
 * for a Free user one quiet line "Meeting Notes: X min left this month"
 * (counting down while recording; web and Mac only, never the phone). There
 * is no live transcript in any mode on the web. On end the page waits for the
 * notepad's notes to be linked, then opens the meeting, which shows "Writing
 * your notes…" with those notes under it (MeetingNotesTierView). Full
 * Intelligence keeps this recorder as it was.
 *
 * @author DES
 * @date 28-07-2026
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { showBillingErrorToast } from "@/features/my-billing/billingToast";
import { Bookmark, CircleCheckBig, LoaderCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Avatar } from "@/components/ui/Avatar";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { useAuth } from "@/shared/auth/auth-context";
import { apiPost } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import type { DesktopCommand, DesktopSession } from "@/shared/desktop/desktopShell";
import { useDesktopSession } from "@/shared/desktop/useDesktopSession";
import { toast } from "@/shared/hooks/useToast";
import { getInitials } from "@/shared/utils/formatters";
import { preauthorizeBoardroom, releaseBoardroomHold, type PreauthResult } from "./billingPreauth";
import { ensureAiConsent } from "@/features/ai-consent/consentBridge";
import {
  BOARDROOM_RECORDER_CARD_BACKGROUND,
  BOARDROOM_RECORDER_PREVIEW_BOOKMARKS,
  BOARDROOM_RECORDER_PREVIEW_ELAPSED_MS,
  BOARDROOM_RECORDER_PREVIEW_MIME,
  BOARDROOM_RECORDER_PREVIEW_PARTICIPANTS,
  BOARDROOM_RECORDER_PREVIEW_TITLE,
  BOARDROOM_RECORDER_SHOW_CARD,
  BOARDROOM_RECORDER_UI_PREVIEW,
  recorderDockOn,
} from "./boardroomRecorderUiPreview";
import { RecorderDock } from "./RecorderDock";
import { EditableMeetingTitle } from "./EditableMeetingTitle";
import {
  PREVIEW_AGENDA_NOTE,
  PREVIEW_WRITING_NOTE,
  seedPreviewNotes,
} from "./boardroomRecorderPreviewNotes";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/shared/theme/cn";
import { deleteDraft, getDraft } from "./capture/boardroomDraftStore";
import { boardroomAudioConstraints } from "./capture/captureProfile";
import type {
  BoardroomDraftBookmark,
  BoardroomDraftParticipant,
  BoardroomMeetingTier,
} from "./capture/boardroomDraftStore";
import { startMicMeter } from "./capture/micMeter";
import { useDurableRecorder } from "./capture/useDurableRecorder";
import {
  uploadAndCreateBoardroomMeeting,
  type BoardroomUploadPhase,
} from "./captureApi";
import { ListeningMicButton } from "./ListeningMicButton";
import { RecorderAgendaPanel } from "./RecorderAgendaPanel";
import { toggleAgendaCheck, type AgendaCheck } from "./agendaChecks";
import { useRecorderAgendaChecks } from "./useRecorderAgendaChecks";
import { MeetingNotesPanel } from "@/features/notes/MeetingNotesPanel";
import { useBoardroomBasePath } from "./useBoardroomBasePath";
import { useMySubscription } from "@/features/my-billing/api";
import { freeNotesLeftLine } from "./recorderPriceHints";
import { useLiveUsage } from "./useLiveUsage";
import { previewLiveUsageFetcher } from "./dev/previewLiveUsage";
import {
  FREE_RECORDING_STOPPED_MESSAGE,
  includedLeftLine,
  includedUsedNotice,
} from "@/features/my-billing/freeMinutes";
import type { FreeRecordingWindow } from "./billingPreauth";
import { useRecorderLeaveGuard } from "./useRecorderLeaveGuard";

type RecordNavState = {
  title?: string;
  targetLanguages?: string[];
  participants?: BoardroomDraftParticipant[];
  deviceId?: string;
  /** Desktop app: mix call audio in (set on the setup screen). */
  includeSystemAudio?: boolean;
  resumeDraftId?: string;
  scheduledMeetingId?: string;
  /** GDPR recording notice from the setup slide / scheduled confirm (06-10-2026). */
  recordingNotice?: { method: "setup_slide" | "scheduled_confirm"; at: string };
  /** Agenda picked on the setup screen (ADR-023). Older callers. */
  prepNoteId?: string | null;
  /** Every agenda picked on the setup screen (30-09-2026). */
  prepNoteIds?: string[];
  /** Meeting Notes or full (ADR-030, 06-10-2026). Absent = full: a scheduled
   *  start and a recovered draft's Continue carry none (the draft keeps its
   *  own, see useDurableRecorder). */
  tier?: BoardroomMeetingTier;
};

/** The agendas a navigation carried, from either field. */
function navAgendaIds(nav: RecordNavState): string[] {
  if (nav.prepNoteIds && nav.prepNoteIds.length > 0) return nav.prepNoteIds;
  return nav.prepNoteId ? [nav.prepNoteId] : [];
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function uploadPhaseLabel(phase: BoardroomUploadPhase | "assembling"): string {
  switch (phase) {
    case "assembling":
      return "Saving recording…";
    case "authorizing":
      return "Preparing secure upload…";
    case "uploading":
      return "Uploading audio…";
    case "creating":
      return "Starting AI processing…";
  }
}

/** Air kept between the sheet's top and the shell header. */
const STICKY_GAP = 24;

const SAVE_HINT =
  "Don't close or refresh. Audio saves locally every few seconds; recover from meetings if the browser dies.";

/** lg breakpoint, for the compact orb in the two-column layout. */
function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

export function BoardroomRecorderPage() {
  const { user } = useAuth();
  // lg: the controls are a sheet on the left. sm..lg: orb AND controls share
  // one tray at the bottom, so the page above is all agenda. Phones: the orb
  // stays in the page and only the controls form the tray. (30-09-2026)
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isSmUp = useMediaQuery("(min-width: 640px)");

  // Desktop sheet (30-09-2026, DES): a bottom sheet glued to the top of the
  // shell footer (and filling behind it). `fixed`, placed over the left grid column (measured from
  // `sheetSlot`), with its bottom at the footer's height as measured ONCE, so
  // it moves neither when the page scrolls nor when the footer slides away.
  // Capped to the space under the header; a taller sheet scrolls inside.
  // State, not refs: both mount only after boot.
  const [sheetSlot, setSheetSlot] = useState<HTMLDivElement | null>(null);
  const [sheetBox, setSheetBox] = useState<{
    left: number;
    width: number;
    bottom: number;
    paddingBottom: number;
    maxHeight: number;
  } | null>(null);
  useEffect(() => {
    if (!isDesktop || !sheetSlot) {
      setSheetBox(null);
      return;
    }
    const footerH = document.querySelector("footer")?.offsetHeight ?? 0;
    const measure = () => {
      const r = sheetSlot.getBoundingClientRect();
      // The shell pads its scroll root by the full header height.
      const root = sheetSlot.closest<HTMLElement>("[data-scroll-root]");
      const headerH = root ? parseFloat(getComputedStyle(root).paddingTop) : 0;
      // Runs to the screen's bottom edge with the footer's height as bottom
      // padding: the controls sit just above the footer, and when the footer
      // slides away the sheet's own fill shows there, not white.
      setSheetBox({
        left: r.left,
        width: r.width,
        bottom: 0,
        paddingBottom: footerH + 16,
        maxHeight: window.innerHeight - headerH - STICKY_GAP,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sheetSlot);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isDesktop, sheetSlot]);

  // Phones (below lg): the action tray is FIXED to the screen's bottom edge,
  // with the footer's height as bottom padding, so it sits just above the
  // footer and its fill (not white, not page content) shows when the footer
  // slides away (30-09-2026). Measured: the footer is taller on phones.
  const [trayEl, setTrayEl] = useState<HTMLDivElement | null>(null);
  const [wrapEl, setWrapEl] = useState<HTMLDivElement | null>(null);
  const [trayH, setTrayH] = useState(0);
  const [trayFooterPad, setTrayFooterPad] = useState(0);
  useEffect(() => {
    // From sm up the whole controls block (orb included) is the tray.
    const tray = isSmUp ? wrapEl : trayEl;
    if (isDesktop || !tray) return;
    const footer = document.querySelector("footer");
    const measure = () => {
      setTrayFooterPad(footer?.offsetHeight ?? 0);
      setTrayH(tray.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    // border-box: the footer padding changes the tray's height, not its
    // content box, and a default observer would miss it.
    ro.observe(tray, { box: "border-box" });
    if (footer) ro.observe(footer);
    return () => ro.disconnect();
  }, [isDesktop, isSmUp, trayEl, wrapEl]);
  const navigate = useNavigate();
  const base = useBoardroomBasePath();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nav = (location.state ?? {}) as RecordNavState;
  const isUiPreview =
    BOARDROOM_RECORDER_UI_PREVIEW || searchParams.get("uiPreview") === "1";
  const [booting, setBooting] = useState(!isUiPreview);
  // The dock (RecorderDock.tsx) at every window size since W10 (22-10-2026);
  // compact under lg. The old dial sheet below is reachable only in local
  // dev with `?dock=0` (recorderDockOn), to compare.
  const dockMode = recorderDockOn();
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<
    BoardroomUploadPhase | "assembling"
  >("assembling");
  const [previewStatus, setPreviewStatus] = useState<"recording" | "held">(
    "recording",
  );
  const [previewElapsedMs, setPreviewElapsedMs] = useState(
    BOARDROOM_RECORDER_PREVIEW_ELAPSED_MS,
  );
  const [previewBookmarks, setPreviewBookmarks] = useState<
    BoardroomDraftBookmark[]
  >(BOARDROOM_RECORDER_PREVIEW_BOOKMARKS);
  // The local /__preview page (stand-in user): sample notes for the agenda,
  // its picker and "Your notes" (boardroomRecorderPreviewNotes.ts).
  const isPreviewPage = isUiPreview && location.pathname.startsWith("/__preview");
  const qc = useQueryClient();
  useState(() => {
    if (isPreviewPage) seedPreviewNotes(qc);
    return null;
  });
  const [previewAgendaNoteIds, setPreviewAgendaNoteIds] = useState<string[]>(
    () => (isPreviewPage ? [PREVIEW_AGENDA_NOTE.id] : navAgendaIds(nav)),
  );

  const {
    state: liveState,
    start,
    hold,
    resume,
    addBookmark,
    setPrepNoteIds,
    setAgendaChecks: setLiveAgendaChecks,
    setTitle: setDraftTitle,
    stopAndAssemble,
    discard,
    audioLevelRef: liveAudioLevelRef,
  } = useDurableRecorder();

  // B49: the preview's own tick list (nothing is recorded or uploaded there).
  const [previewAgendaChecks, setPreviewAgendaChecks] = useState<AgendaCheck[] | null>(null);
  const setAgendaChecks = isUiPreview
    ? (update: (prev: AgendaCheck[] | null) => AgendaCheck[] | null) => setPreviewAgendaChecks(update)
    : setLiveAgendaChecks;

  /** Preview opens mic for glow only — separate from durable capture stream. */
  const previewAudioLevelRef = useRef(0);
  const audioLevelRef = isUiPreview ? previewAudioLevelRef : liveAudioLevelRef;

  const state = isUiPreview
    ? {
        draftId: "preview",
        status: previewStatus,
        elapsedMs: previewElapsedMs,
        bookmarks: previewBookmarks,
        error: null as string | null,
        mimeLabel: BOARDROOM_RECORDER_PREVIEW_MIME,
        prepNoteIds: previewAgendaNoteIds,
        agendaChecks: previewAgendaChecks,
        // `&tier=notes` opens the preview as Meeting Notes (DES 11-10-2026).
        tier: (nav.tier === "notes" || searchParams.get("tier") === "notes"
          ? "notes"
          : "full") as BoardroomMeetingTier,
      }
    : liveState;
  const isNotes = state.tier === "notes";
  // Free users: Meeting Notes minutes left this month (ADR-045 `freePlan`),
  // counting down with the recording. Web + Mac only (the phone shows no
  // prices or allowances to personal users, ADR-043).
  const subscription = useMySubscription();
  // Live included minutes (B5, 22-10-2026): display only, never stops the
  // recorder; 404 / offline = no line. The preview uses `?live=` stubs.
  const previewLive = isUiPreview ? previewLiveUsageFetcher(searchParams.get("live")) : null;
  const live = useLiveUsage({
    tier: isNotes ? "notes" : "full",
    active:
      (state.status === "recording" || state.status === "held") && (!isUiPreview || !!previewLive),
    elapsedMs: state.elapsedMs,
    fetcher: previewLive ?? undefined,
  });
  // Once this recording used the last included minute, the server's
  // sentence (it keeps recording) stays in place of "0 left".
  const freeNotesLine =
    includedUsedNotice(live) ??
    includedLeftLine(live) ??
    // B43: Free Full Intelligence counts down the same free minutes.
    freeNotesLeftLine(subscription.data?.freePlan, state.elapsedMs, isNotes ? "notes" : "full");

  // Meeting Notes: a title typed in Take notes names the meeting when Setup
  // gave none (DES 07-10-2026: "if they want to they can"). Starts as the
  // Setup title, which the field shows prefilled.
  const [notesTitle, setNotesTitle] = useState(() => nav.title?.trim() ?? "");
  const meetingTitleToSave = nav.title?.trim() || (isNotes ? notesTitle.trim() : "");

  // Stable keys — avoid re-boot loops from new `nav` / `user` object identities.
  const userId = user?.id;
  const billingAudience = billingAudienceFor(user);
  const bootTitle = nav.title;
  const bootResumeId = nav.resumeDraftId;
  const bootDeviceId = nav.deviceId;
  const bootSystemAudio = !!nav.includeSystemAudio;
  const bootScheduledId = nav.scheduledMeetingId;
  const bootNoticeKey = JSON.stringify(nav.recordingNotice ?? null);
  const bootAgendaKey = JSON.stringify(navAgendaIds(nav));
  const bootLangsKey = JSON.stringify(nav.targetLanguages ?? ["en-GB"]);
  const bootPartsKey = JSON.stringify(nav.participants ?? []);
  const bootTier: BoardroomMeetingTier = nav.tier === "notes" ? "notes" : "full";
  // Where leaving goes (DES 07-10-2026: discarding a Meeting Notes recording
  // landed on the Boardrooms list). Meeting Notes live in the Notes Library;
  // the draft's tier wins once it is known, the route's before that.
  const exitPath = (isNotes || bootTier === "notes") ? `${base}/notes` : `${base}/meetings`;

  // Always guard while this route is mounted — including boot / preview / idle.
  // Status-gated guards stayed silent on Safari when capture hadn't reached
  // "recording" yet (or preview chrome looked active but status checks failed).
  const leaveGuardActive = true;

  const {
    leaveDialogOpen,
    allowLeave,
    stayOnPage,
    confirmLeave,
    setLeaveDialogOpen,
  } = useRecorderLeaveGuard(leaveGuardActive, exitPath);

  // One card hold per visit to this page (21-10-2026). The boot effect can run
  // twice (React StrictMode in dev, or a dependency changing): each run used to
  // place its own hold, and the second opened a second 3DS window while the
  // first was still up ("You have an in-flight handleNextAction!"). Now every
  // run shares this one; refs live across StrictMode's re-run.
  const holdRef = useRef<Promise<PreauthResult> | null>(null);
  /** The hold placed on THIS visit (not a resumed draft's), until recording starts with it. */
  const freshHoldRef = useRef<string | null>(null);
  // B43 (31-10-2026): a Free live recording warns at 5 free minutes left and
  // stops by itself FREE_GRACE_MINUTES after the pool; null = never cut.
  const freeWindowRef = useRef<FreeRecordingWindow | null>(null);
  const freeWarnedRef = useRef(false);
  const freeStoppedRef = useRef(false);
  const startedRef = useRef(false);
  const leavingRef = useRef(false);
  const mountedRef = useRef(false);
  const releaseTimerRef = useRef<number | null>(null);
  // Left before recording started: give the fresh hold back. Deferred a tick
  // so StrictMode's simulated unmount (followed at once by a mount) does not.
  useEffect(() => {
    mountedRef.current = true;
    if (releaseTimerRef.current != null) window.clearTimeout(releaseTimerRef.current);
    return () => {
      mountedRef.current = false;
      releaseTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) return;
        const pi = freshHoldRef.current;
        freshHoldRef.current = null;
        if (pi) void releaseBoardroomHold(pi);
      }, 0);
    };
  }, []);

  // UI preview: live timer (freezes on hold) so the mic chrome can be styled.
  useEffect(() => {
    if (!isUiPreview || previewStatus !== "recording") return;
    const id = window.setInterval(() => {
      setPreviewElapsedMs((ms) => ms + 250);
    }, 250);
    return () => window.clearInterval(id);
  }, [isUiPreview, previewStatus]);

  // UI preview: open mic for meter-reactive glow (no MediaRecorder / billing).
  useEffect(() => {
    if (!isUiPreview) return;
    let cancelled = false;
    let stop: (() => void) | null = null;
    let stream: MediaStream | null = null;

    void (async () => {
      try {
        // Same raw profile as live capture — preview glow must read like the
        // real thing (AGC off is quieter than Chrome's default processing).
        stream = await navigator.mediaDevices.getUserMedia({
          audio: boardroomAudioConstraints(),
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const meter = startMicMeter(stream, previewAudioLevelRef);
        stop = () => {
          meter.stop();
          stream?.getTracks().forEach((t) => t.stop());
        };
      } catch {
        /* Glow stays dark if permission denied — preview chrome still usable. */
      }
    })();

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [isUiPreview]);

  useEffect(() => {
    if (isUiPreview) {
      setBooting(false);
      return;
    }

    let cancelled = false;

    const boot = async () => {
      if (!userId) return;
      // No setup state at all = opened directly; back to setup. An EMPTY
      // title is valid (setup says "Optional, we'll name it for you").
      if (bootTitle === undefined && !bootResumeId) {
        navigate(`${base}/meetings/new`, { replace: true });
        return;
      }

      setBooting(true);

      // "How VERBLIZR uses AI" before anything is recorded (B7, 22-10-2026):
      // Boardroom and Notes both start here. "Not now" = straight back, no
      // card hold, nothing recorded.
      if (!(await ensureAiConsent())) {
        if (cancelled) return;
        leavingRef.current = true;
        allowLeave();
        navigate(exitPath, { replace: true });
        return;
      }
      if (cancelled) return;

      try {
        let paymentIntentId: string | null = null;
        let authorizedCents: number | null = null;

        if (bootResumeId) {
          const existing = await getDraft(bootResumeId);
          if (cancelled) return;
          if (existing?.paymentIntentId) {
            paymentIntentId = existing.paymentIntentId;
            authorizedCents = existing.authorizedCents ?? null;
          }
        }

        if (!paymentIntentId) {
          // Shared by every run of this effect: one hold, one bank check.
          holdRef.current ??= preauthorizeBoardroom(bootTier).then((r) => {
            if (r.ok && r.paymentIntentId) {
              // Left during the bank check: the page is gone, give it back.
              if (!mountedRef.current) void releaseBoardroomHold(r.paymentIntentId);
              else freshHoldRef.current = r.paymentIntentId;
            }
            return r;
          });
          const preauth = await holdRef.current;
          if (cancelled) return;
          if (!preauth.ok) {
            // The refusal names the screen that fixes it: the user's own cards,
            // the company Billing page, or "ask your admin" (17-10-2026).
            const shown = showBillingErrorToast({
              code: preauth.error,
              audience: billingAudience,
              serverMessage: preauth.message,
              details: preauth.details,
              navigate: (to) => navigate(to),
            });
            if (!shown) {
              toast({
                variant: "destructive",
                title: "Billing authorization failed",
                description: preauth.message ?? "Could not authorize billing. Please try again.",
              });
            }
            // Nothing was recorded: straight back, no "Leave this recording?",
            // and the recorder (its "Recording" label) never shows.
            leavingRef.current = true;
            allowLeave();
            navigate(exitPath, { replace: true });
            return;
          }
          paymentIntentId = preauth.paymentIntentId ?? null;
          authorizedCents = preauth.authorizedCents ?? null;
          freeWindowRef.current = preauth.freeWindow ?? null;
        }

        if (cancelled || startedRef.current) return;
        startedRef.current = true;

        const targetLanguages = JSON.parse(bootLangsKey) as string[];
        const participants = JSON.parse(
          bootPartsKey,
        ) as BoardroomDraftParticipant[];

        const began = await start({
          userId,
          // A Notes recording without a title is "Meeting notes", not a
          // boardroom (07-10-2026; it surfaced on the interrupted banner).
          title: bootTitle || (bootTier === "notes" ? "Meeting notes" : "Boardroom Session"),
          targetLanguages:
            targetLanguages.length > 0 ? targetLanguages : ["en-GB"],
          participants,
          deviceId: bootDeviceId,
          includeSystemAudio: bootSystemAudio,
          resumeDraftId: bootResumeId,
          paymentIntentId,
          authorizedCents,
          scheduledMeetingId: bootScheduledId ?? null,
          recordingNotice: JSON.parse(bootNoticeKey),
          prepNoteIds: JSON.parse(bootAgendaKey) as string[],
          tier: bootTier,
        });
        // The hold now belongs to the recording (and its draft); a start
        // that never recorded gives a fresh hold back.
        const fresh = freshHoldRef.current;
        freshHoldRef.current = null;
        if (!began && fresh) void releaseBoardroomHold(fresh);
      } catch (err) {
        const fresh = freshHoldRef.current;
        freshHoldRef.current = null;
        if (fresh) void releaseBoardroomHold(fresh);
        if (!cancelled) {
          toast({
            variant: "destructive",
            title: "Could not start recording",
            description:
              err instanceof Error ? err.message : "Please try again.",
          });
          leavingRef.current = true;
          allowLeave();
          navigate(exitPath, { replace: true });
        }
      } finally {
        // Always clear spinner for this attempt. React Strict Mode cancels the
        // first mount mid-flight — leaving booting=true forever was the stuck UI.
        // Leaving: keep the boot screen until the page is gone.
        if (!cancelled && !leavingRef.current) setBooting(false);
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [
    base,
    bootDeviceId,
    bootSystemAudio,
    bootAgendaKey,
    bootLangsKey,
    bootPartsKey,
    bootResumeId,
    bootScheduledId,
    bootNoticeKey,
    bootTitle,
    bootTier,
    isUiPreview,
    navigate,
    start,
    userId,
    billingAudience,
    allowLeave,
  ]);

  // Renamed on this page (the dock layout's editable heading, 11-10-2026).
  const [editedTitle, setEditedTitle] = useState<string | null>(null);
  const renameMeeting = (title: string) => {
    const next = title.trim().slice(0, 200);
    if (!next) return;
    setEditedTitle(next);
    if (isNotes) setNotesTitle(next);
    if (!isUiPreview) setDraftTitle(next);
  };
  const displayTitle = editedTitle
    ? editedTitle
    : isNotes
    ? meetingTitleToSave || "Meeting notes"
    : isUiPreview
      ? nav.title || searchParams.get("title") || BOARDROOM_RECORDER_PREVIEW_TITLE
      : nav.title || "Boardroom";
  const participants = isUiPreview
    ? nav.participants?.length
      ? nav.participants
      : BOARDROOM_RECORDER_PREVIEW_PARTICIPANTS
    : (nav.participants ?? []);
  // The header shows the host's own avatar first; the roster now carries
  // the host too (BoardroomSetupPage `withHost`), so leave them out here.
  const otherParticipants = participants.filter(
    (p) => p.email.toLowerCase() !== (user?.email ?? "").toLowerCase(),
  );

  const canBookmark = state.status === "recording" || state.status === "held";

  // The notepad's clock (ADR-030, 06-10-2026): the second of the recording a
  // line was typed at. A ref, so MeetingNotesPanel reads the latest tick
  // without re-rendering on it.
  const elapsedMsRef = useRef(state.elapsedMs);
  elapsedMsRef.current = state.elapsedMs;
  const getElapsedSecs = () => elapsedMsRef.current / 1000;

  // B49: agenda check boxes. Loaded while recording (not Meeting Notes, which
  // has no agenda); ticks are frozen once the recording stops.
  const agendaTickable = canBookmark && !uploading;
  useRecorderAgendaChecks({
    noteIds: state.prepNoteIds,
    enabled: !isNotes && agendaTickable,
    draftId: state.draftId,
    setAgendaChecks,
  });
  const onAgendaToggle = (key: string) => {
    if (!agendaTickable) return;
    const at = getElapsedSecs();
    setAgendaChecks((prev) => (prev ? toggleAgendaCheck(prev, key, at) : prev));
  };

  // Notes written or attached in the desktop widget during the meeting
  // (02-10-2026). Kept per draft so a recovered recording still has them, and
  // linked PRIVATELY (`addendum`) once the meeting exists: they are the
  // writer's own, never input to the shared Meeting Intel.
  const meetingNotesKey = state.draftId ? `vz.meetingNotes.${state.draftId}` : null;
  const [meetingNotes, setMeetingNotes] = useState<{ ids: string[]; writing: string | null }>({
    ids: [],
    writing: null,
  });
  useEffect(() => {
    if (!meetingNotesKey) return;
    try {
      const raw = window.localStorage.getItem(meetingNotesKey);
      const parsed = raw ? (JSON.parse(raw) as { ids?: unknown; writing?: unknown }) : null;
      if (!parsed && isPreviewPage) {
        setMeetingNotes({ ids: [PREVIEW_WRITING_NOTE.id], writing: PREVIEW_WRITING_NOTE.id });
        return;
      }
      setMeetingNotes({
        ids: Array.isArray(parsed?.ids) ? parsed.ids.filter((x): x is string => typeof x === "string") : [],
        writing: typeof parsed?.writing === "string" ? parsed.writing : null,
      });
    } catch {
      /* private mode: the list just starts empty */
    }
  }, [meetingNotesKey, isPreviewPage]);
  const updateMeetingNotes = (ids: string[], writing: string | null) => {
    const next = { ids: [...new Set(ids)], writing };
    setMeetingNotes(next);
    if (!meetingNotesKey) return;
    try {
      window.localStorage.setItem(meetingNotesKey, JSON.stringify(next));
    } catch {
      /* best effort */
    }
  };


  const onEnd = async () => {
    if (isUiPreview) {
      setUploading(true);
      setPreviewStatus("held");
      setUploadPhase("assembling");
      setUploadPercent(5);
      const steps: Array<{
        phase: BoardroomUploadPhase | "assembling";
        percent: number;
        ms: number;
      }> = [
        { phase: "assembling", percent: 12, ms: 1200 },
        { phase: "authorizing", percent: 22, ms: 1400 },
        { phase: "uploading", percent: 55, ms: 2200 },
        { phase: "uploading", percent: 82, ms: 2000 },
        { phase: "creating", percent: 100, ms: 1600 },
      ];
      for (const step of steps) {
        setUploadPhase(step.phase);
        setUploadPercent(step.percent);
        await new Promise((r) => window.setTimeout(r, step.ms));
      }
      toast({
        variant: "success",
        title: "Preview complete",
        description:
          "Live flow uploads audio, starts AI, then opens the meeting.",
      });
      setUploading(false);
      setUploadPercent(0);
      setPreviewStatus("recording");
      return;
    }
    setUploading(true);
    setUploadPhase("assembling");
    setUploadPercent(4);
    try {
      const assembled = await stopAndAssemble();
      if (!assembled || assembled.blob.size < 256) {
        toast({
          variant: "destructive",
          title: "Recording too short",
          description: "Capture a bit more audio, then try again.",
        });
        setUploading(false);
        setUploadPercent(0);
        return;
      }
      setUploadPercent(12);
      // Meeting Notes: the Take notes title, when Setup gave none.
      // A rename on this page wins over Setup's title (11-10-2026).
      const titleOverride =
        editedTitle || (assembled.meta.tier === "notes" ? meetingTitleToSave : "");
      const upload = titleOverride
        ? { ...assembled, meta: { ...assembled.meta, title: titleOverride } }
        : assembled;
      const meeting = await uploadAndCreateBoardroomMeeting(
        upload,
        (progress) => {
          setUploadPhase(progress.phase);
          setUploadPercent(progress.percent);
        },
      );
      // In-meeting notes: private links, fire-and-forget like the agenda's (a
      // failed link must never fail a recording that is already uploaded; the
      // note can still be attached from the meeting afterwards).
      // Awaited since 20-10-2026 (calm recorder): the meeting page shows
      // these notes under "Writing your notes…", so they must be linked
      // before it opens. Still never fails the recording.
      await Promise.allSettled(
        meetingNotes.ids.map((noteId) =>
          apiPost(boardroomEndpoints.prepNotes(meeting.id), {
            noteId,
            role: "addendum",
          }).catch((err) => console.warn("[Recorder] Meeting note link failed:", noteId, err)),
        ),
      );
      if (meetingNotesKey) {
        try {
          window.localStorage.removeItem(meetingNotesKey);
        } catch {
          /* ignore */
        }
      }
      await deleteDraft(assembled.meta.draftId);
      // A Meeting Notes recording has no speaker naming (ADR-030): its page
      // shows processing, then the notes.
      // Meeting Notes: no toast, the page itself says "Writing your notes…".
      if (assembled.meta.tier !== "notes") {
        toast({
          variant: "success",
          title: "Uploaded",
          description: "Diarization started — tag speakers when ready.",
        });
      }
      allowLeave();
      navigate(`${base}/meetings/${meeting.id}`, { replace: true });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description:
          err instanceof Error
            ? err.message
            : isNotes
              ? "Your draft is still saved. Open Notes Library to Process."
              : "Your draft is still saved. Open Meeting Intel to Process.",
      });
      allowLeave();
      navigate(exitPath, { replace: true });
    } finally {
      setUploading(false);
      setUploadPercent(0);
    }
  };

  // B43: the Free grace (web + Mac, same as the phone). The warning once, then
  // End by itself at the stop time: what was recorded uploads and processes
  // like a normal End, nothing is lost. Paid plans have no window.
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  useEffect(() => {
    const w = freeWindowRef.current;
    if (!w || isUiPreview || state.status !== "recording" || uploading) return;
    const secs = state.elapsedMs / 1000;
    if (!freeWarnedRef.current && secs >= w.warnAfterSeconds && w.warningMessage) {
      freeWarnedRef.current = true;
      toast({ title: "Free minutes", description: w.warningMessage });
    }
    if (!freeStoppedRef.current && secs >= w.stopAfterSeconds) {
      freeStoppedRef.current = true;
      toast({ title: "Recording stopped", description: FREE_RECORDING_STOPPED_MESSAGE });
      void onEndRef.current();
    }
  }, [state.elapsedMs, state.status, uploading, isUiPreview]);

  // `confirmed`: the desktop widget already asked in its own popup.
  const onCancel = async (confirmed = false) => {
    if (isUiPreview) {
      allowLeave();
      navigate(exitPath, { replace: true });
      return;
    }
    if (!confirmed && !window.confirm("Discard this recording?")) return;
    // Read the hold BEFORE the draft goes, then give it back (02-10-2026):
    // a discarded meeting used to leave its pre-authorization on the card.
    const draftId = state.draftId;
    const held = draftId ? await getDraft(draftId) : null;
    await discard();
    void releaseBoardroomHold(held?.paymentIntentId);
    if (meetingNotesKey) {
      try {
        window.localStorage.removeItem(meetingNotesKey);
      } catch {
        /* ignore */
      }
    }
    allowLeave();
    navigate(exitPath, { replace: true });
  };

  const onMicTap = () => {
    if (isUiPreview) {
      setPreviewStatus((s) => (s === "recording" ? "held" : "recording"));
      return;
    }
    if (state.status === "recording") void hold();
    else if (state.status === "held") void resume();
  };

  const onAddBookmark = (type: "decision" | "todo") => {
    if (isUiPreview) {
      const secs = Math.floor(previewElapsedMs / 1000);
      setPreviewBookmarks((prev) => [
        ...prev,
        {
          id: `preview-${type}-${Date.now()}`,
          type,
          timeInSecs: secs,
          timestamp: formatElapsed(previewElapsedMs),
        },
      ]);
      return;
    }
    addBookmark(type);
  };

  const onAgendaChange = (noteIds: string[]) => {
    if (isUiPreview) {
      setPreviewAgendaNoteIds(noteIds);
      return;
    }
    // The recorder owns this, not the page: it also has to reach the draft, or
    // a recording recovered after a crash would forget the choice.
    setPrepNoteIds(noteIds);
  };

  // The desktop app's floating widget (02-10-2026): this page stays the only
  // owner of the recording; the widget shows its state and sends commands.
  const desktopSession: DesktopSession | null =
    !booting && (state.status === "recording" || state.status === "held" || uploading)
      ? {
          kind: "boardroom",
          title: displayTitle,
          status: uploading ? "busy" : state.status === "held" ? "paused" : "live",
          elapsedMs: state.elapsedMs,
          at: Date.now(),
          running: state.status === "recording" && !uploading,
          bookmarks: state.bookmarks.map((b) => ({ type: b.type, timestamp: b.timestamp })),
          agendaNoteIds: state.prepNoteIds,
          // B49: the widget's agenda popup ticks through `agenda-tick`.
          agendaChecks: isNotes ? undefined : (state.agendaChecks ?? undefined),
          meetingNoteIds: meetingNotes.ids,
          writingNoteId: meetingNotes.writing,
          tier: state.tier,
          notesTitle: isNotes ? notesTitle : undefined,
        }
      : null;
  useDesktopSession(desktopSession, (cmd: DesktopCommand) => {
    if (cmd.type === "toggle-hold") {
      if (!uploading) onMicTap();
    } else if (cmd.type === "bookmark") {
      if (canBookmark && !uploading) onAddBookmark(cmd.kind);
    } else if (cmd.type === "end") {
      if (!uploading && state.status !== "idle") void onEnd();
    } else if (cmd.type === "discard") {
      if (!uploading) void onCancel(true);
    } else if (cmd.type === "agenda-tick") {
      onAgendaToggle(cmd.key);
    } else if (cmd.type === "set-notes") {
      updateMeetingNotes(cmd.meetingNoteIds, cmd.writingNoteId);
    } else if (cmd.type === "set-title") {
      // The widget's Take notes title (Meeting Notes only).
      if (isNotes) setNotesTitle(cmd.title);
    }
  });

  if (booting) {
    return (
      <div className="mx-auto flex min-h-[calc(100dvh-8.5rem)] w-full max-w-lg flex-col items-center justify-center gap-3 px-2 py-6">
        <LoaderCircle className="h-8 w-8 animate-spin text-brand" />
        <p className="text-sm text-muted-foreground">
          Authorizing billing &amp; opening microphone…
        </p>
      </div>
    );
  }

  // Live Session Marks: in the sheet, or on the page beside the dock.
  const marksBlock = (
    !isNotes && state.bookmarks.length > 0 ? (
                <div className="w-full min-w-0 space-y-2">
                  <h3 className="text-sm font-semibold tracking-tight text-foreground">
                    Live Session Marks
                  </h3>
                  <div
                    className="-mx-1 overflow-x-auto overscroll-x-contain px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    role="list"
                    aria-label="Live session marks"
                  >
                    <div className="flex touch-pan-x items-center">
                      {state.bookmarks.map((b, index) => {
                        const isDecision = b.type === "decision";
                        const isLast = index === state.bookmarks.length - 1;
                        return (
                          <div
                            key={b.id}
                            role="listitem"
                            className="flex shrink-0 items-center"
                          >
                            <span
                              className={
                                isDecision
                                  ? "mr-2 h-2 w-2 rounded-full bg-brand ring-[1.5px] ring-brand/20"
                                  : "mr-2 h-2 w-2 rounded-full bg-foreground ring-[1.5px] ring-foreground/20"
                              }
                              aria-hidden
                            />
                            <span
                              className={
                                isDecision
                                  ? "inline-flex h-[30px] items-center gap-1 rounded-full bg-brand/[0.05] py-0 pl-1.5 pr-2.5 text-brand"
                                  : "inline-flex h-[30px] items-center gap-1 rounded-full bg-background py-0 pl-1.5 pr-2.5 text-foreground"
                              }
                              title={
                                isDecision
                                  ? `Key Decision at ${formatElapsed(b.timeInSecs * 1000)}`
                                  : `To-do at ${formatElapsed(b.timeInSecs * 1000)}`
                              }
                            >
                              {isDecision ? (
                                <Bookmark className="h-3.5 w-3.5 fill-brand text-brand" />
                              ) : (
                                <CircleCheckBig className="h-3.5 w-3.5 text-foreground" />
                              )}
                              {/* Same colours as the Key Decision / To-do buttons
                                  above (DES 11-10-2026): fill, icon and time. */}
                              <span className="text-[11px] font-semibold tabular-nums">
                                {formatElapsed(b.timeInSecs * 1000)}
                              </span>
                            </span>
                            {!isLast ? (
                              <span
                                className="mx-2 h-[1.5px] w-5 shrink-0 bg-border"
                                aria-hidden
                              />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null
  );

  return (
    <>
      {/* Two columns from lg (30-09-2026, DES): the recording controls on the
        left fit a small laptop screen; the agenda, which can run long, gets
        the right column instead of pushing the slide off-screen. Phones keep
        the single column. From lg the pair starts at the left, not centred
        in a wide window (DES 11-10-2026: "kafi right per hai"). */}
      <div
        className={cn(
          "flex min-h-[calc(100dvh-8.5rem)] w-full items-center justify-center py-4 lg:min-h-0 lg:items-start lg:justify-start lg:py-2",
          // Dock under lg: the page reads from the top, as from lg.
          dockMode && "min-h-0 items-start justify-start py-2",
        )}
      >
        {/* Card chrome gated by BOARDROOM_RECORDER_SHOW_CARD — kept for later. */}
        <div
          ref={setGridEl}
          className={cn(
            BOARDROOM_RECORDER_SHOW_CARD
              ? "relative flex w-full min-w-0 max-w-lg flex-col items-center gap-6 overflow-hidden rounded-3xl p-5 shadow-sm sm:p-6 lg:grid lg:max-w-5xl lg:grid-cols-2 lg:items-start lg:gap-x-10 lg:gap-y-4"
              : "relative flex w-full min-w-0 max-w-lg flex-col items-center gap-6 overflow-visible p-5 sm:p-6 lg:grid lg:max-w-5xl lg:grid-cols-2 lg:items-start lg:gap-x-10 lg:gap-y-4 lg:py-2",
            // Dock: the agenda and notes use the page's full width (DES
            // 11-10-2026: "is portion ko end tak poora hona chaiyeh").
            dockMode && "max-w-none items-stretch",
          )}
          style={
            BOARDROOM_RECORDER_SHOW_CARD
              ? { background: BOARDROOM_RECORDER_CARD_BACKGROUND }
              : undefined
          }
        >
          {uploading ? (
            /* Over the whole window (05-10-2026, DES: "processing kidhar
               chupa howa hai"): inside the two-column box it sat centred
               between the columns, and the desktop sheet (fixed, also z-30,
               later in the page) covered its left half. */
            <div
              className={
                BOARDROOM_RECORDER_SHOW_CARD
                  ? "absolute inset-0 z-40 flex flex-col items-center justify-center gap-4 rounded-3xl bg-background/80 px-8 backdrop-blur-sm"
                  : "fixed inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-background/80 px-8 backdrop-blur-sm"
              }
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <LoaderCircle className="h-8 w-8 animate-spin text-brand" />
              <div className="w-full max-w-xs space-y-2 text-center">
                <p className="text-sm font-semibold text-foreground">
                  {uploadPhaseLabel(uploadPhase)}
                </p>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={uploadPercent}
                  aria-label="Upload progress"
                >
                  <div
                    className="h-full rounded-full bg-brand transition-[width] duration-300 ease-out"
                    style={{ width: `${uploadPercent}%` }}
                  />
                </div>
                <p className="text-xs font-medium tabular-nums text-muted-foreground">
                  {uploadPercent}%
                </p>
              </div>
              <p className="max-w-[16rem] text-center text-[12px] font-medium leading-snug text-foreground">
                Don&apos;t close or refresh this window. The upload can fail if
                you leave now.
              </p>
              <p className="max-w-[16rem] text-center text-[11px] leading-snug text-muted-foreground">
                {isUiPreview
                  ? "Preview only. A real meeting opens when this finishes."
                  : "Audio is already saved locally; you can recover from Meeting Intel if something goes wrong."}
              </p>
            </div>
          ) : null}
          {/* Desktop: the controls as ONE sheet (30-09-2026, DES: "mobile
              per yeh screen hi hai, i want like a sheet here"). Handle, round
              corners, soft fill and shadow, slides up on open, sticky while
              the agenda scrolls. `top`, not a transform, follows the header
              collapse (a transform shifted it while still in normal flow).
              Below lg it is `contents`, so phones keep one column. */}
          {/* TRIAL (DES 11-10-2026): no shadow in DARK only; light keeps
              its shadow, without it the sheet did not stand off the page
              ("light theme main shadow kay baghair yeh different nahin
              lagraha"). Dark was dark:lg:shadow-[0_-8px_24px_rgba(0,0,0,0.3)]. */}
          {/* Holds the left column's place in the grid; the fixed sheet
              reads its position and width. */}
          <div
            ref={setSheetSlot}
            aria-hidden
            className={cn("hidden lg:col-start-1 lg:row-start-1 lg:block", dockMode && "!hidden")}
          />
          <div
            ref={setWrapEl}
            className={cn("contents motion-reduce:animate-none sm:fixed sm:inset-x-0 sm:bottom-0 sm:z-30 sm:grid sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] sm:items-center sm:gap-6 sm:rounded-t-[20px] sm:bg-secondary sm:px-6 sm:pt-4 sm:shadow-[0_-6px_12px_rgba(0,0,0,0.08)] dark:sm:shadow-none dark:sm:bg-card lg:right-auto lg:flex lg:flex-col lg:gap-4 lg:overflow-hidden lg:rounded-t-[28px] lg:bg-secondary lg:px-6 lg:pb-4 lg:pt-3 lg:shadow-[0_-8px_24px_rgba(0,0,0,0.06)] dark:lg:shadow-none lg:animate-in lg:fade-in-0 lg:slide-in-from-bottom-6 dark:lg:bg-card", dockMode && "!hidden")}
            style={
              isDesktop
                ? (sheetBox ?? undefined)
                : isSmUp
                  ? { paddingBottom: trayFooterPad + 8 }
                  : undefined
            }
          >
            <span
              aria-hidden
              className="mx-auto hidden h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/25 lg:block"
            />
            {/* On a short screen only this part scrolls; the goggles and
                the slide below always stay in view. */}
            <div className="order-1 flex w-full flex-col items-center gap-6 lg:min-h-0 lg:shrink lg:gap-4 lg:overflow-y-auto">
              <header className="flex w-full flex-col items-center gap-2.5 text-center">
                <span
                  className={
                    state.status === "held"
                      ? "inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground"
                      : "inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-brand"
                  }
                >
                  <span
                    aria-hidden
                    className={
                      state.status === "held"
                        ? "h-1.5 w-1.5 rounded-full bg-muted-foreground/55"
                        : isNotes
                          ? "h-1.5 w-1.5 rounded-full bg-brand"
                          : "h-1.5 w-1.5 animate-pulse rounded-full bg-brand"
                    }
                  />
                  {state.status === "held" ? "On hold" : isNotes ? "Listening" : "Recording"}
                </span>
                <h1 className="max-w-full px-2 text-[1.375rem] font-bold leading-snug tracking-tight text-foreground sm:text-2xl">
                  {displayTitle}
                </h1>
                {/* No avatar row in Meeting Notes: it is shared with no one. */}
                {!isNotes && (otherParticipants.length > 0 || user) && (
                  <div className="flex items-center -space-x-2 pt-0.5">
                    {user ? (
                      <Avatar
                        src={user.profileImageUrl}
                        alt={user.name || user.email}
                        initials={getInitials(user.name || user.email)}
                        size="md"
                        className="ring-2 ring-background/90"
                      />
                    ) : null}
                    {otherParticipants.slice(0, 6).map((p) => (
                      <Avatar
                        key={p.email}
                        src={p.profileImageUrl}
                        alt={p.name}
                        initials={getInitials(p.name)}
                        size="md"
                        className="ring-2 ring-background/90"
                      />
                    ))}
                    {otherParticipants.length > 6 ? (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-xs font-semibold ring-2 ring-background/90">
                        +{otherParticipants.length - 6}
                      </span>
                    ) : null}
                  </div>
                )}
              </header>

              <ListeningMicButton
                // Meeting Notes: the roomier ring box on a wide screen (calm
                // recorder); the tablet sheet's 15rem column keeps compact.
                compact={isSmUp && !(isNotes && isDesktop)}
                elapsedLabel={formatElapsed(state.elapsedMs)}
                elapsedMs={state.elapsedMs}
                status={state.status}
                audioLevelRef={audioLevelRef}
                disabled={uploading}
                onTap={onMicTap}
                hint={
                  state.status === "recording"
                    ? "Tap to hold"
                    : state.status === "held"
                      ? "Tap to resume"
                      : null
                }
              />

              {freeNotesLine ? (
                <p className="text-center text-[13px] text-muted-foreground">{freeNotesLine}</p>
              ) : null}
              {state.error ? (
                <p className="text-center text-sm text-destructive">
                  {state.error}
                </p>
              ) : (
                <p className="max-w-[16rem] text-center text-[11px] leading-snug text-muted-foreground sm:hidden">
                  {SAVE_HINT}
                </p>
              )}
            </div>
            {/* Phones: Action Goggles, marks and the slide docked to the
              bottom as the mobile app's tray (whiteBottomChrome). Inside the
              sheet on desktop, where the tray styling resets. */}
            <div
              ref={setTrayEl}
              className="fixed inset-x-0 bottom-0 z-30 order-3 flex flex-col items-center gap-5 rounded-t-[20px] bg-secondary px-6 pt-4 shadow-[0_-6px_12px_rgba(0,0,0,0.08)] dark:bg-card sm:static sm:inset-auto sm:z-auto sm:w-full sm:gap-4 sm:rounded-none sm:bg-transparent sm:p-0 sm:shadow-none dark:sm:bg-transparent lg:shrink-0 [&>*]:max-w-lg sm:[&>*]:max-w-none"
              style={isSmUp ? undefined : { paddingBottom: trayFooterPad + 8 }}
            >
              {/* No marks in Meeting Notes (DES 07-10-2026): the to-dos are
                  found from the transcript, so there is nothing to press
                  while talking. */}
              {isNotes ? null : (
              <div className="w-full space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                  <h2 className="text-sm font-semibold tracking-tight text-foreground">
                    Action Goggles
                  </h2>
                  <p className="text-[11px] text-muted-foreground">
                    · Bookmark / Timestamps Markers.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={!canBookmark}
                    onClick={() => onAddBookmark("decision")}
                    className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-brand/[0.05] px-3 text-[13px] font-bold text-brand transition-[transform,background-color,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:bg-brand/15 hover:shadow-sm active:translate-y-0 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                  >
                    <Bookmark className="h-5 w-5 shrink-0 fill-brand text-brand" />
                    Key Decision
                  </button>
                  <button
                    type="button"
                    disabled={!canBookmark}
                    onClick={() => onAddBookmark("todo")}
                    className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-background px-3 text-[13px] font-bold text-foreground transition-[transform,background-color,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:bg-muted hover:shadow-sm active:translate-y-0 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                  >
                    <CircleCheckBig className="h-5 w-5 shrink-0 text-foreground" />
                    {/* "To-do" (DES 07-10-2026, the phone's c22f7e3). */}
                    To-do
                  </button>
                </div>
              </div>
              )}

              {marksBlock}

              <div className="flex w-full flex-col gap-2 pt-2">
                <SwipeButton
                  text={isNotes ? "Slide to end" : "Slide to End & Process AI"}
                  loading={uploading}
                  loadingText="Processing…"
                  disabled={uploading || state.status === "idle"}
                  onSwipeComplete={() => void onEnd()}
                  accessibilityLabel={
                    isNotes ? "End recording and write notes" : "End recording and process AI"
                  }
                  accessibilityHint={
                    isNotes
                      ? "Slide or press Enter to end the recording; your notes are written from what was said"
                      : "Slide or press Enter to end the meeting and start AI processing"
                  }
                />
                {/* A quiet grey link, like every sheet's Cancel (DES
                    07-10-2026). No ring box: when the window regains focus
                    the browser could put the red keyboard ring on it, which
                    read as Discard being selected. Keyboard focus shows as
                    an underline instead. */}
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => void onCancel()}
                  className="mx-auto rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground focus-visible:underline disabled:pointer-events-none disabled:opacity-50"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>

          {/* Phones: room at the end of the page for the fixed tray, so the
              agenda's last lines never sit behind it. */}
          <div
            aria-hidden
            className="order-4 w-full shrink-0 lg:hidden"
            style={{
              height: isDesktop || dockMode ? 0 : Math.max(0, trayH - trayFooterPad),
            }}
          />

          <div
            className={
              dockMode
                ? isNotes
                  ? // Meeting Notes: no agenda or marks, so the notes take the
                    // whole width, no divider (DES 11-10-2026).
                    // Room under the page for the dock and its minutes line.
                    "order-2 w-full space-y-3 pb-40 lg:col-span-2"
                  : "order-2 w-full space-y-3 pb-40 lg:col-span-2 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] lg:items-start lg:gap-x-10 lg:gap-y-3 lg:space-y-0"
                : "order-2 w-full space-y-3 lg:col-start-2 lg:row-start-1"
            }
          >
            {/* Dock: the full meeting title as the page's heading, across both
                columns; the dock's own title stays short (DES 11-10-2026: "main
                page per title kahan show hota hai"). Wraps, never cut. */}
            {dockMode ? (
              <div className="mb-2 lg:col-span-2">
                <EditableMeetingTitle
                  title={displayTitle}
                  disabled={uploading}
                  onSave={renameMeeting}
                />
              </div>
            ) : null}
            {!state.error ? (
              <p className={cn("hidden text-[11px] leading-snug text-muted-foreground sm:block", dockMode && "block")}>
                {SAVE_HINT}
              </p>
            ) : null}
            {/* Above Action Goggles, not inside it: that section MARKS the
              recording, this one only reads. */}
            {/* No agenda in Meeting Notes (ADR-030): nothing checks it. */}
            {isNotes ? null : (
              <RecorderAgendaPanel
                noteIds={state.prepNoteIds}
                onChange={onAgendaChange}
              />
            )}
            {/* Dock: the marks live on the page, under the agenda. */}
            {dockMode && state.bookmarks.length > 0 ? <div className="pt-2">{marksBlock}</div> : null}
            {/* Notes during the meeting (02-10-2026), same panel as the
                desktop widget's: private, linked when the meeting ends. Not
                in the UI preview, where nothing is uploaded to link them to,
                except the local /__preview page (stand-in user, not signed
                in, so nothing saves; DES 11-10-2026). */}
            {state.draftId && (!isUiPreview || isPreviewPage) ? (
              // No card around it (DES 07-10-2026): the notepad reads as the
              // Write a Note page, a title and numbered points on the page.
              // Dock: the notes take the wide right column.
              // Dock: a hairline divides it from the agenda (DES 11-10-2026),
              // centred in the gap: 40px of gap, the line, 40px of padding.
              <div
                className={cn(
                  "pt-2",
                  dockMode &&
                    !isNotes &&
                    "lg:col-start-2 lg:row-span-4 lg:row-start-2 lg:min-h-[60vh] lg:border-l lg:border-border lg:pl-10 lg:pt-0",
                )}
              >
                <MeetingNotesPanel
                  key={state.draftId}
                  tone="page"
                  meetingTitle={displayTitle}
                  agendaNoteIds={state.prepNoteIds}
                  meetingNoteIds={meetingNotes.ids}
                  writingNoteId={meetingNotes.writing}
                  onChange={updateMeetingNotes}
                  getElapsedSecs={getElapsedSecs}
                  {...(isNotes
                    ? {
                        heading: "Take notes",
                        allowAttach: false,
                        titleField: { initial: notesTitle, onChange: setNotesTitle },
                      }
                    : {})}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {dockMode ? (
        <RecorderDock
          anchor={gridEl}
          title={displayTitle}
          status={state.status}
          elapsedMs={state.elapsedMs}
          elapsedLabel={formatElapsed(state.elapsedMs)}
          people={[
            ...(user
              ? [
                  {
                    key: "me",
                    name: user.name || user.email,
                    photo: user.profileImageUrl,
                    initials: getInitials(user.name || user.email),
                  },
                ]
              : []),
            ...otherParticipants.map((p) => ({
              key: p.email,
              name: p.name,
              photo: p.profileImageUrl,
              initials: getInitials(p.name),
            })),
          ]}
          isNotes={isNotes}
          canBookmark={canBookmark}
          uploading={uploading}
          onMicTap={onMicTap}
          onAddBookmark={onAddBookmark}
          onEnd={() => void onEnd()}
          onDiscard={() => void onCancel()}
          audioLevelRef={audioLevelRef}
          compact={!isDesktop}
          note={freeNotesLine}
          error={state.error}
          agendaChecks={isNotes ? null : state.agendaChecks}
          agendaLocked={!agendaTickable}
          onAgendaToggle={onAgendaToggle}
        />
      ) : null}

      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave this recording?</AlertDialogTitle>
            <AlertDialogDescription>
              Going back, refreshing, or opening another page can interrupt
              capture. Audio already saved locally can be recovered from Meeting
              Intel — stay here if you are still recording.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={stayOnPage}>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
